import * as THREE from "three";
import { InstanceBatch } from "./instance_batch.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { Vfx3DGlobalAtlas } from "../textures/vfx_3d_global_atlas.js";

import { AttributeSize } from "../types/core_types.js";

export const PlanarWaveLayout = {
  aPosTime:    AttributeSize.Vec4, // x, y, z, start_time
  aVelDur:     AttributeSize.Vec4, // vx, vy, vz, duration
  aSizeParams: AttributeSize.Vec4, // width, height, fade_start, max_alpha
  aColorDecel: AttributeSize.Vec4, // r, g, b, deceleration_power
  aUvBounds:   AttributeSize.Vec4, // min_x, min_y, max_x, max_y
} as const;

export class VfxPlanarWaveBatch extends InstanceBatch<typeof PlanarWaveLayout>
{
  private static readonly MAX_INSTANCES : number = 100;
  private static readonly RENDER_ORDER  : number = 21;

  private static cached_geometry : THREE.BufferGeometry | null = null;
  private texture_initialized    : boolean = false;

  public constructor()
  {
    const base_geometry = VfxPlanarWaveBatch.getGeometry();

    let atlas_texture: THREE.Texture | null = null;
    try { atlas_texture = Vfx3DGlobalAtlas.getTexture(); } catch ( _e ) {}

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uTime:    { value: 0.0 },
        uTexture: { value: atlas_texture },
      },
      vertexShader: `
        uniform float uTime;

        attribute vec4 aPosTime;
        attribute vec4 aVelDur;
        attribute vec4 aSizeParams;
        attribute vec4 aColorDecel;
        attribute vec4 aUvBounds;

        varying vec2 vUv;
        varying vec3 vColor;
        varying float vAlpha;

        void main() {
          float start_time = aPosTime.w;
          float duration = aVelDur.w;
          float age = max( 0.0, uTime - start_time );
          float progress = clamp( age / duration, 0.0, 1.0 );

          vUv = mix( aUvBounds.xy, aUvBounds.zw, uv );
          vColor = aColorDecel.rgb;

          // Alpha fade precomputed per vertex
          float fade_start = aSizeParams.z;
          float fade = progress > fade_start ? 1.0 - clamp( ( progress - fade_start ) / max( 1.0 - fade_start, 0.001 ), 0.0, 1.0 ) : 1.0;
          float fade_in = clamp( progress / 0.04, 0.0, 1.0 );
          vAlpha = aSizeParams.w * fade * fade_in;

          // Deceleration curve for realistic burst
          float deceleration_power = aColorDecel.w;
          float ease_dist = 1.0 - pow( 1.0 - progress, deceleration_power );
          vec3 computed_pos = aPosTime.xyz + aVelDur.xyz * duration * ease_dist;

          // 2D flight direction in view space (modelViewMatrix)
          vec3 view_vel = ( modelViewMatrix * vec4( aVelDur.xyz, 0.0 ) ).xyz;
          vec2 dir = length( view_vel.xy ) > 0.001 ? normalize( view_vel.xy ) : vec2( 0.0, 1.0 );

          // Rotate quad so +Y points along velocity vector
          mat2 rot = mat2(
            dir.y, -dir.x,
            dir.x,  dir.y
          );

          vec2 local_scaled = position.xy * aSizeParams.xy;
          vec2 rotated_pos = rot * local_scaled;

          vec3 billboard_local = vec3( rotated_pos, 0.0 );
          vec4 mv_position = modelViewMatrix * vec4( computed_pos, 1.0 );
          mv_position.xyz += billboard_local;
          gl_Position = projectionMatrix * mv_position;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;

        varying vec2 vUv;
        varying vec3 vColor;
        varying float vAlpha;

        void main() {
          vec4 sample_color = texture2D( uTexture, vUv );
          gl_FragColor = vec4( vColor * sample_color.rgb, sample_color.a * vAlpha );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.FrontSide,
    } );

    super( { max_instances: VfxPlanarWaveBatch.MAX_INSTANCES, layout: PlanarWaveLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = VfxPlanarWaveBatch.RENDER_ORDER;
  }

  private static getGeometry(): THREE.BufferGeometry
  {
    if ( VfxPlanarWaveBatch.cached_geometry )
      return VfxPlanarWaveBatch.cached_geometry;

    const geom = new THREE.PlaneGeometry( 1.0, 1.0 );
    VfxPlanarWaveBatch.cached_geometry = geom;
    return geom;
  }

  public spawnParticle(
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    width: number,
    height: number,
    fade_start_progress: number,
    start_time: number,
    duration: number,
    max_alpha: number,
    color: THREE.Color = new THREE.Color( 0xffffff ),
    res_id: ResourceID = ResourceID.WaveGlow1,
    deceleration_power: number = 2.0
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !this.texture_initialized || !mat.uniforms.uTexture.value )
    {
      try
      {
        mat.uniforms.uTexture.value = Vfx3DGlobalAtlas.getTexture();
        this.texture_initialized = true;
      }
      catch ( _e ) {}
    }

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const wave_uv = Vfx3DGlobalAtlas.getUVRect( res_id );

    const pos_time_attr    = this.geometry.getAttribute( "aPosTime" ).array as Float32Array;
    const vel_dur_attr     = this.geometry.getAttribute( "aVelDur" ).array as Float32Array;
    const size_params_attr = this.geometry.getAttribute( "aSizeParams" ).array as Float32Array;
    const color_decel_attr = this.geometry.getAttribute( "aColorDecel" ).array as Float32Array;
    const uv_bounds_attr   = this.geometry.getAttribute( "aUvBounds" ).array as Float32Array;

    pos_time_attr[ idx * 4 ]     = start_pos.x;
    pos_time_attr[ idx * 4 + 1 ] = start_pos.y;
    pos_time_attr[ idx * 4 + 2 ] = start_pos.z;
    pos_time_attr[ idx * 4 + 3 ] = start_time;

    vel_dur_attr[ idx * 4 ]     = velocity.x;
    vel_dur_attr[ idx * 4 + 1 ] = velocity.y;
    vel_dur_attr[ idx * 4 + 2 ] = velocity.z;
    vel_dur_attr[ idx * 4 + 3 ] = duration;

    size_params_attr[ idx * 4 ]     = width;
    size_params_attr[ idx * 4 + 1 ] = height;
    size_params_attr[ idx * 4 + 2 ] = fade_start_progress;
    size_params_attr[ idx * 4 + 3 ] = max_alpha;

    color_decel_attr[ idx * 4 ]     = color.r;
    color_decel_attr[ idx * 4 + 1 ] = color.g;
    color_decel_attr[ idx * 4 + 2 ] = color.b;
    color_decel_attr[ idx * 4 + 3 ] = deceleration_power;

    uv_bounds_attr[ idx * 4 ]     = wave_uv.min_x;
    uv_bounds_attr[ idx * 4 + 1 ] = wave_uv.min_y;
    uv_bounds_attr[ idx * 4 + 2 ] = wave_uv.max_x;
    uv_bounds_attr[ idx * 4 + 3 ] = wave_uv.max_y;

    this.geometry.getAttribute( "aPosTime" ).needsUpdate    = true;
    this.geometry.getAttribute( "aVelDur" ).needsUpdate     = true;
    this.geometry.getAttribute( "aSizeParams" ).needsUpdate = true;
    this.geometry.getAttribute( "aColorDecel" ).needsUpdate = true;
    this.geometry.getAttribute( "aUvBounds" ).needsUpdate   = true;

    return this.getStableId( idx );
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}
